'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import toast, { type Toast } from 'react-hot-toast';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Loader2,
  MessageSquare,
  X,
} from 'lucide-react';

export type ToastVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'loading'
  | 'message'
  | 'achievement';

export interface UnifiedToastProps {
  t: Toast;
  variant: ToastVariant;
  title: string;
  description?: string;
  categoryLabel?: string;
  icon?: React.ReactNode;
  accentColor?: string;
  avatar?: string;
  sender?: string;
  actionLabel?: string;
  onAction?: () => void;
  onClick?: () => void;
  showCloseButton?: boolean;
}

interface VariantConfig {
  border: string;
  text: string;
  iconBg: string;
  icon: React.ReactNode;
}

const VARIANT_DEFAULTS: Record<string, VariantConfig> = {
  success: {
    border: 'border-green-500',
    text: 'text-green-400',
    iconBg: 'bg-green-500/10',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  error: {
    border: 'border-red-500',
    text: 'text-red-400',
    iconBg: 'bg-red-500/10',
    icon: <XCircle className="w-4 h-4" />,
  },
  warning: {
    border: 'border-amber-500',
    text: 'text-amber-400',
    iconBg: 'bg-amber-500/10',
    icon: <AlertTriangle className="w-4 h-4" />,
  },
  info: {
    border: 'border-blue-500',
    text: 'text-blue-400',
    iconBg: 'bg-blue-500/10',
    icon: <Info className="w-4 h-4" />,
  },
  loading: {
    border: 'border-blue-500',
    text: 'text-blue-400',
    iconBg: 'bg-blue-500/10',
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
  },
};

// Tailwind safelist — these dynamic accent classes are used by achievement rarity:
// border-gray-500 border-green-500 border-blue-500 border-purple-500 border-amber-400
// text-gray-400 text-green-400 text-blue-400 text-purple-400 text-amber-300

/**
 * Derive a lighter text class from an accent color stem.
 * e.g. 'green-500' → 'text-green-400', 'amber-400' → 'text-amber-300'
 */
function accentToTextClass(accent: string): string {
  return `text-${accent.replace(/-(\d+)$/, (_, n) => `-${Math.max(300, Number(n) - 100)}`)}`;
}

/**
 * A toast is portaled away from where the user is working, so a dismissed toast that holds focus (its action
 * or close button) would drop focus to <body>. `restore` hands focus back to the last element focused outside
 * the toast, and only when the toast itself holds focus (WCAG 2.4.3).
 */
function useToastFocusReturn() {
  const rootRef = useRef<HTMLDivElement>(null);
  const previousRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const remember = (target: EventTarget | null) => {
      if (target instanceof HTMLElement && target !== document.body && !rootRef.current?.contains(target)) previousRef.current = target;
    };
    remember(document.activeElement);
    const onFocusIn = (event: FocusEvent) => remember(event.target);
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);
  const restore = useCallback(() => {
    const previous = previousRef.current;
    if (previous?.isConnected && rootRef.current?.contains(document.activeElement)) previous.focus();
  }, []);
  return { rootRef, restore };
}

export function UnifiedToast({
  t,
  variant,
  title,
  description,
  categoryLabel,
  icon: iconOverride,
  accentColor,
  avatar,
  sender,
  actionLabel,
  onAction,
  onClick,
  showCloseButton,
}: UnifiedToastProps) {
  const { rootRef, restore } = useToastFocusReturn();
  const isMessage = variant === 'message';
  const config = VARIANT_DEFAULTS[variant] ?? null;

  // --- Colors (same derivation for every variant) ---
  const borderClass = accentColor
    ? `border-${accentColor}`
    : config?.border ?? 'border-blue-500';
  const textClass = accentColor
    ? accentToTextClass(accentColor)
    : config?.text ?? 'text-blue-400';
  const iconBgClass = config?.iconBg ?? 'bg-white/5';

  // --- Icon: always a 9×9 circle so every variant occupies the same space ---
  let iconNode: React.ReactNode;
  if (iconOverride) {
    const inner =
      typeof iconOverride === 'string' ? (
        <span className="text-lg leading-none">{iconOverride}</span>
      ) : (
        iconOverride
      );
    iconNode = (
      <div
        className={`w-9 h-9 rounded-full ${iconBgClass} flex items-center justify-center flex-shrink-0 ${textClass}`}
      >
        {inner}
      </div>
    );
  } else if (isMessage) {
    // Avatar fallback. If the caller passed an explicit `avatar` string, use
    // it. Otherwise derive initials from the sender name (up to two letters);
    // last resort is a neutral chat-bubble emoji. The old fallback was a
    // police-officer emoji which falsely labeled every incoming message as
    // coming from an officer, including caller messages.
    const initials = !avatar && sender
      ? sender
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0]?.toUpperCase())
          .filter(Boolean)
          .join('')
      : '';
    iconNode = (
      <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 text-lg leading-none">
        {avatar || initials || '💬'}
      </div>
    );
  } else if (config) {
    iconNode = (
      <div
        className={`w-9 h-9 rounded-full ${iconBgClass} flex items-center justify-center flex-shrink-0 ${textClass}`}
      >
        {config.icon}
      </div>
    );
  }

  // --- Category label (message uses sender as label) ---
  const displayLabel = isMessage ? sender : categoryLabel;

  // --- Close button: shown on error/warning by default ---
  const shouldShowClose =
    showCloseButton ?? (variant === 'error' || variant === 'warning');

  // --- Click handler ---
  const isClickable = !!(onClick || isMessage);
  const handleClick = () => {
    if (onClick) onClick();
    restore();
    toast.dismiss(t.id);
  };

  // ====== Single unified layout for ALL variants ======
  return (
    <div
      ref={rootRef}
      className={`${
        t.visible ? 'animate-enter' : 'animate-leave'
      } max-w-sm w-full pointer-events-auto rounded-xl border-2 ${borderClass} bg-popup shadow-2xl${
        isClickable ? ' cursor-pointer' : ''
      }`}
      onClick={isClickable ? handleClick : undefined}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon / Avatar */}
          {iconNode}

          {/* Content. Bounded height, scrolling inside: a toast is transient
              chrome over the working surface, and an unbounded one (the
              after-call "Archive failed: …" listing every file) grew to the
              full viewport height over the report's Actions column, so the
              officer's complete buttons underneath could not be clicked (owner
              report 2026-09-23, "Complete doesn't always work"). Nothing is cut:
              the text scrolls, and every error/warning toast is also retained in
              the notification bell (lib/notifications/retention.ts). The region
              is focusable so the scrolled part is reachable by keyboard. */}
          <div
            className="flex-1 min-w-0 max-h-[35vh] overflow-y-auto break-words"
            data-testid="unified-toast-content"
            tabIndex={0}
            aria-label="Notification text"
          >
            {/* Category label row */}
            {displayLabel && (
              <div className="flex items-center gap-2 mb-0.5">
                <span
                  className={`text-xs font-semibold uppercase ${textClass} truncate`}
                >
                  {displayLabel}
                </span>
                {isMessage && (
                  <span className="px-1.5 py-0.5 bg-blue-600 text-white rounded text-[10px] font-semibold uppercase flex-shrink-0">
                    New
                  </span>
                )}
              </div>
            )}

            {/* Title */}
            <div className="text-sm font-bold text-white">
              {title}
            </div>

            {/* Description */}
            {description && (
              <div className="text-xs text-gray-400 mt-0.5">{description}</div>
            )}

            {/* Action button */}
            {actionLabel && onAction && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAction();
                  restore();
                  toast.dismiss(t.id);
                }}
                className={`text-xs ${textClass} underline mt-1.5 hover:opacity-80 transition-opacity`}
              >
                {actionLabel}
              </button>
            )}

            {/* Message hint */}
            {isMessage && (
              <div className="flex items-center gap-1 mt-1.5 text-[11px] text-gray-400">
                <MessageSquare className="w-3 h-3" />
                <span>Tap to view</span>
              </div>
            )}
          </div>

          {/* Close button */}
          {shouldShowClose && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                restore();
                toast.dismiss(t.id);
              }}
              className="text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
